/**
 * The model's price estimate (`product.priceRange`, a string the page's
 * generation wrote: "$950-1200 CAD") against what we have since observed.
 *
 * Why (Search QA 2026-10-19, item 2, causes C and D): the estimate was written
 * before any lookup ran and nothing ever revisited it. Cards then printed
 * "ESTIMATED $200 - $250" beside an amazon.ca button whose listing charged
 * $159.99, and "ESTIMÉ 500 $ - 600 $" under a pizza oven sold at $895.98.
 *
 * Two rules, both over live data and neither a repair of stored content:
 *
 *  - Render: a card prints the estimate only when it shows no observed price,
 *    and never when any observation we hold for the product in this market,
 *    at any store, falls outside the range by more than ESTIMATE_TOLERANCE.
 *    An observation is a price the page may show: the product's own price
 *    (after presentProduct) and the priced offers rows in the market's
 *    currency, Amazon's only while its licence lets us show them (24 hours
 *    from the read; an older Amazon price is not used even as a hidden check,
 *    the strict reading of the licence).
 *  - Write: when a lookup or a refresh files a price for a card, the stored
 *    range is kept when that price confirms it, rewritten from the
 *    observations when two stores or more disagree with it, and cleared
 *    otherwise. A cleared range is the empty string, not null: the
 *    page_products upsert keeps the old column value on a null
 *    (COALESCE(EXCLUDED.price_range, ...)), and every reader already treats
 *    an empty string as no range.
 *
 * The parser is the one the cards render with (formerly
 * frontend/lib/format-price-range.ts, which re-exports it): one parser, so a
 * range the check cannot read is also one the card prints raw, as before.
 *
 * Pure and client-safe. Never throws.
 */

import type { Product } from "../types/product.type";
import { REGION_FOR_CODE, type RegionCode } from "../types/region.type";
import { getCurrencyForRegion } from "./currency";
import { normalizeMerchantDomain } from "./offers/merchant-domain";
import { isAmazonHost, isCurrentAmazonRow } from "./present-product-core";

/** How far outside the estimate an observation may fall before the estimate is hidden (20%). */
export const ESTIMATE_TOLERANCE = 0.2;

const CURRENCY_TOKEN_RE =
  /(?:\$|€|£|¥|CAD|USD|GBP|EUR|AUD|NZD|JPY|CHF|SEK|PLN|BRL|MXN|INR|ARS|COP|CLP|CA|US)\.?/gi;

/** Parse "$950-1200 CAD" / "€90–120" / "£15 to 25 GBP"; null when unsure. */
export function parsePriceRange(raw: string | null | undefined): { min: number; max: number } | null {
  if (!raw || typeof raw !== "string") return null;
  const cleaned = raw.replace(CURRENCY_TOKEN_RE, " ").trim();
  // NOTE: \b is ASCII-only in JS, so "à" needs explicit whitespace delimiters.
  const parts = cleaned
    .split(/\s*[-–—]\s*|\s+(?:to|à)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length !== 2) return null;
  const toNumber = (part: string): number | null => {
    // Reject anything beyond a plain number with thousands/decimal separators.
    if (!/^\d[\d\s.,]*$/.test(part)) return null;
    const normalized = part.replace(/[\s,]/g, "");
    const value = Number(normalized);
    return Number.isFinite(value) && value > 0 ? value : null;
  };
  const min = toNumber(parts[0]);
  const max = toNumber(parts[1]);
  if (min === null || max === null) return null;
  if (min > max || max >= 10_000_000) return null;
  return { min, max };
}

/** Inside the range, give or take the tolerance at each end. */
export function priceWithinEstimate(
  amount: number,
  range: { min: number; max: number },
  tolerance: number = ESTIMATE_TOLERANCE,
): boolean {
  return amount >= range.min * (1 - tolerance) && amount <= range.max * (1 + tolerance);
}

/** One price we hold for the product in this market, and whose it is. */
export interface PriceObservation {
  amount: number;
  currency: string;
  /** The store's host, lower-cased, without "www.". */
  store: string;
}

function marketCurrency(regionCode: string | null | undefined): string | null {
  const region = REGION_FOR_CODE[(regionCode ?? "").toUpperCase() as RegionCode];
  return region ? getCurrencyForRegion(region) : null;
}

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function isSearchLink(link: Product["externalLinks"] | undefined): boolean {
  if (!link?.href) return true;
  if (link.type === "amazon-search") return true;
  try {
    const parsed = new URL(link.href);
    return isAmazonHost(parsed.hostname) && (/^\/s(\/|$)/.test(parsed.pathname) || parsed.searchParams.has("k"));
  } catch {
    return true;
  }
}

type OfferRow = NonNullable<Product["offers"]>[number];

/** A row whose price a shopper could pay: not an information link, not a list, member or hidden price. */
function isPricedOfferRow(offer: OfferRow | null | undefined): offer is OfferRow & { price: { value: number; currency: string } } {
  if (!offer || offer.kind === "info") return false;
  if (offer.priceKind !== undefined && offer.priceKind !== "sale" && offer.priceKind !== "regular") return false;
  const value = offer.price?.value;
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * The prices we hold for the product in this market, as the page may show
 * them: the product's own price beside its buy link (none beside an Amazon
 * search), and every priced offers row in the market's currency. An Amazon
 * row counts only while it is current (isCurrentAmazonRow): `now` is the
 * server's clock; without it (the browser, after the server's check) any
 * parseable read time counts. An out-of-stock row still counts: its price is
 * a price that store asked.
 */
export function priceObservations(
  product: Pick<Product, "price" | "externalLinks" | "offers">,
  regionCode: string | null | undefined,
  now?: number,
): PriceObservation[] {
  const out: PriceObservation[] = [];
  try {
    const currency = marketCurrency(regionCode);
    const link = product.externalLinks;
    const own = [product.price?.listingPrice, product.price?.lowestPrice].find(
      (n) => typeof n === "number" && Number.isFinite(n) && n > 0,
    );
    const host = hostOf(link?.href);
    if (own !== undefined && host && !isSearchLink(link)) {
      out.push({ amount: own, currency: currency ?? "", store: normalizeMerchantDomain(host) });
    }
    for (const offer of product.offers ?? []) {
      if (!isPricedOfferRow(offer)) continue;
      if (currency && offer.price.currency?.toUpperCase() !== currency) continue;
      const store = normalizeMerchantDomain(offer.merchantDomain ?? "");
      if (!store) continue;
      if ((isAmazonHost(store) || offer.source === "creators-api") && !isCurrentAmazonRow(offer, now)) continue;
      out.push({ amount: offer.price.value, currency: offer.price.currency, store });
    }
  } catch {
    // Pure: a malformed product yields what was read before the fault.
  }
  return out;
}

/**
 * Does any observation we hold fall outside the stored estimate by more than
 * the tolerance? False when there is no estimate, when it does not parse, or
 * when nothing has been observed.
 */
export function estimateContradicted(
  product: Pick<Product, "priceRange" | "price" | "externalLinks" | "offers">,
  regionCode: string | null | undefined,
  now?: number,
): boolean {
  const range = parsePriceRange(product.priceRange);
  if (!range) return false;
  return priceObservations(product, regionCode, now).some((observation) => !priceWithinEstimate(observation.amount, range));
}

/**
 * The estimate a card with no observed price may print: the stored string,
 * trimmed, or '' when there is none or when an observation contradicts it.
 * The caller decides whether the card shows an observed price (then it never
 * prints the estimate) and renders the string in the reader's locale.
 */
export function priceEstimateToShow(
  product: Pick<Product, "priceRange" | "price" | "externalLinks" | "offers">,
  regionCode: string | null | undefined,
  now?: number,
): string {
  const raw = typeof product.priceRange === "string" ? product.priceRange.trim() : "";
  if (!raw) return "";
  return estimateContradicted(product, regionCode, now) ? "" : raw;
}

/**
 * A range in the canonical stored shape the generation prompt asks for
 * (default-field-prompts.ts): "$X-Y CAD", "$X-Y", "€X-Y", "£X-Y GBP",
 * "$X-Y AUD", and "X-Y CUR" for every other currency. Whole units: the
 * estimate slot never claims cents.
 */
export function formatStoredPriceRange(min: number, max: number, currency: string): string {
  const low = Math.max(1, Math.floor(min));
  const high = Math.max(low, Math.ceil(max));
  switch (currency.toUpperCase()) {
    case "CAD":
      return `$${low}-${high} CAD`;
    case "USD":
      return `$${low}-${high}`;
    case "EUR":
      return `€${low}-${high}`;
    case "GBP":
      return `£${low}-${high} GBP`;
    case "AUD":
      return `$${low}-${high} AUD`;
    default:
      return `${low}-${high} ${currency.toUpperCase()}`;
  }
}

/**
 * The stored estimate after a writer files a price for the card: the value
 * to store, or undefined when nothing changes.
 *
 *  - no stored range, or one that does not parse, or no observation: nothing;
 *  - every observation inside the range (with the tolerance): kept;
 *  - two stores or more observed and they disagree with it: their
 *    [lowest, highest], in the canonical format;
 *  - otherwise: cleared (the empty string, see the header). One store's
 *    price is not a market range, and an Amazon price may not be kept as
 *    anything once its 24 hours are over.
 *
 * `product` is the card as it is about to be saved, its filed price on it.
 */
export function priceRangeAfterFiling(
  product: Pick<Product, "priceRange" | "price" | "externalLinks" | "offers">,
  regionCode: string | null | undefined,
  now: number = Date.now(),
): string | undefined {
  try {
    const raw = typeof product.priceRange === "string" ? product.priceRange.trim() : "";
    if (!raw) return undefined;
    const range = parsePriceRange(raw);
    if (!range) return undefined;
    const observations = priceObservations(product, regionCode, now);
    if (observations.length === 0) return undefined;
    if (observations.every((observation) => priceWithinEstimate(observation.amount, range))) return undefined;
    const stores = new Set(observations.map((observation) => observation.store));
    const currency = marketCurrency(regionCode) ?? observations[0].currency;
    if (stores.size >= 2 && currency) {
      const amounts = observations.map((observation) => observation.amount);
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);
      if (max > min) return formatStoredPriceRange(min, max, currency);
    }
    return "";
  } catch {
    return undefined;
  }
}
